import React, { useState, useEffect, useCallback } from "react";
import {
  StyleSheet,
  View,
  StyleProp,
  ViewStyle,
  SectionList,
  InputAccessoryView,
  KeyboardAvoidingView,
  Platform,
  Dimensions,
  ScrollView,
} from "react-native";
import { Section, LeftMessage, RightMessage, Input } from "./components";
import { IConversationMessage } from "src/domains";
import { stringDateFullDynamicFormat, stringTimeFormat } from "src/utils/date";
import LocalText from "src/local";

export type MessageSection = {
  date: string;
  data: IConversationMessage[];
};

const SENDING_MESSAGES_SECTION = "SENDING_MESSAGES_SECTION";

export const buildMessagesSections = (messages: IConversationMessage[]) => {
  let sections: MessageSection[] = [];
  for (let i = 0; i < messages.length; i++) {
    let message = messages[i];
    let sectionIndex = sections.findIndex(
      (section) =>
        section.date ===
        (message.createdAt
          ? stringDateFullDynamicFormat(message.createdAt)
          : SENDING_MESSAGES_SECTION)
    );
    if (sectionIndex !== -1) {
      sections[sectionIndex].data.push(message);
    } else {
      sections.push({
        date: message.createdAt
          ? stringDateFullDynamicFormat(message.createdAt)
          : SENDING_MESSAGES_SECTION,
        data: [message],
      });
    }
  }

  return sections;
};

export type ChatProps = {
  containerStyle?: StyleProp<ViewStyle>;
  userId: string;
  messages: IConversationMessage[];
  onSend?: (props: { text?: string }) => void;
};

export const Chat: React.FC<ChatProps> = ({
  containerStyle,
  userId,
  messages,
  onSend,
}) => {
  const [message, setMessage] = useState<string>("");
  const [sections, setSections] = useState<MessageSection[]>([]);
  const [verticalOffset, setVerticalOffset] = useState<number>(0);
  useEffect(() => {
    setSections(buildMessagesSections(messages));
  }, [messages]);

  const sendHandler = useCallback(() => {
    if (onSend) {
      onSend({ text: message });
    }
    setMessage("");
  }, [message]);
  return (
    <View style={[styles.root, containerStyle]}>
      <SectionList
        inverted
        style={styles.scrollView}
        sections={sections}
        keyExtractor={(item) => item.id}
        renderItem={({ item }) => {
          return (
            <React.Fragment>
              {item.ownerId !== userId ? (
                <LeftMessage
                  text={item.content?.text}
                  imageSource={
                    item.content?.image
                      ? { uri: item.content?.image }
                      : undefined
                  }
                  time={
                    item.createdAt
                      ? stringDateFullDynamicFormat(item.createdAt)
                      : undefined
                  }
                />
              ) : null}
              {item.ownerId === userId ? (
                <RightMessage
                  text={item.content?.text}
                  imageSource={
                    item.content?.image
                      ? { uri: item.content?.image }
                      : undefined
                  }
                  time={
                    item.createdAt
                      ? stringDateFullDynamicFormat(item.createdAt)
                      : undefined
                  }
                />
              ) : null}
            </React.Fragment>
          );
        }}
      />

      {onSend && (
        <Input
          value={message}
          onChangeText={(text) => setMessage(text)}
          placeholder={LocalText.components.chat.messageInputPlaceholder}
          onSend={() => sendHandler()}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  root: {
    flexDirection: "column",
  },
  scrollView: {
    flex: 1,
  },
  flatList: {
    flex: 1,
  },
});
